import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { loadRuntimeConfig } from './api/runtime-config';
import { AppRouter } from './app/router';
import { ToastProvider } from './ui/Toast/Toast';
import './ui/tokens.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Each hook in src/api sets its own `retry` (see `shouldRetryQuery`) —
      // this default only covers a query that forgets to. Refetch-on-focus
      // is off: a ticket queue refetching every window focus is surprising
      // rather than helpful without an explicit "live" affordance.
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root was not found in index.html.');
}

/**
 * Runtime config is resolved BEFORE the first render, not during it. If the
 * app rendered first, the very first queries would fire against the default
 * base URL and a deployment using the absolute-URL escape hatch would see a
 * burst of failed requests on every page load. `loadRuntimeConfig` never
 * rejects — it falls back to same-origin — so there is no failure branch here.
 */
void loadRuntimeConfig().then(() => {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AppRouter />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
